import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useMemo, useRef, useState, useEffect } from 'react';
import { styled, css } from 'styled-components';
import TagsList from './TagsList.js';
import { useMutationTagCreate } from '../mutations/useMutationTagCreate.js';
import { useQueryTags } from '../queries/useQueryTags.js';
import { useMagicSuggestionsContext } from '../../magic/components/MagicSuggestionsContext.js';
import { CloseIcon, SearchIcon } from '@fabric-app/icons/icons';
import { getDefaultRadixPortalContainer } from '@fabric-app/compat';
import { Modal } from '@fabric-app/ui/components/Modal/Modal';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { useInputControls } from '@fabric-app/hooks/useInputControls';
import { isTruthy } from '@fabric-app/utils/guards';
import useModalInactiveState from '@fabric-app/hooks/useModalInactiveState';
import { useRefocusOnBlur } from '@fabric-app/hooks/useRefocusOnBlur';
import { TextInput } from '@fabric-app/ui/components/TextInput/TextInput';
import { useBoolState } from '@fabric-app/hooks/useBooleanState';
import { useFocusOnMount } from '@fabric-app/hooks/useFocusOnMount';
import Divider from '@fabric-app/ui/components/Divider';
import { Flex } from '@fabric-app/ui/components/Flex';
import { IconDesktop16Cross, IconDesktop16MiscMinus } from '@fabric-app/icons/svg';
import { mediaHover, mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { DialogDescription } from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { framerAnimationFade } from '@fabric-app/ui/constants/framerAnimations';

const SubHeader = styled.div.withConfig({
  displayName: "EditTagsModal__SubHeader",
  componentId: "sc-7b52eb6e-0"
})([
  `display:flex;flex-direction:column;padding:12px;border-bottom:1px solid `,
  `;`
], cssVar["color-border-secondary"]);
const StyledScrollArea = styled(ScrollArea.Default).withConfig({
  displayName: "EditTagsModal__StyledScrollArea",
  componentId: "sc-7b52eb6e-1"
})([
  `display:grid;`
]);
const StyledForm = styled.form.withConfig({
  displayName: "EditTagsModal__StyledForm",
  componentId: "sc-7b52eb6e-2"
})([
  `display:flex;flex-direction:column;height:max-content;min-width:0;min-height:0;`
]);
const CloseButton = styled.button.withConfig({
  displayName: "EditTagsModal__CloseButton",
  componentId: "sc-7b52eb6e-3"
})([
  `cursor:pointer;color:`,
  `;transition-property:color,background-color,border-color,text-decoration-color,fill,stroke;transition-duration:0.2s;transition-timing-function:ease-in-out;`
], cssVar["color-text-primary"]);
const EditTagsModalContent = ({ selectedTags = [], indeterminateTags = [], onSelect: onSelectProp, selectedResourcesCount }) => {
  const { isMobileView } = useResponsive();
  const inputState = useInputControls();
  const queryTags = useQueryTags(inputState.value);
  const mutationTagCreate = useMutationTagCreate();
  const onSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (inputState.value.length === 0) return;
    const existingTag = queryTags.data?.find((t) => t.name.toLowerCase() === inputState.value.toLowerCase());
    if (existingTag) {
      onSelectProp?.(existingTag, true);
      return;
    }
    if (mutationTagCreate.isPending) return;
    mutationTagCreate.mutateAsync({
      tagName: inputState.value,
      action: "from-resource"
    }, {
      onSuccess: (tag) => {
        onSelectProp?.(tag, true);
      }
    });
  };
  const { suggestions, popSuggestion } = useMagicSuggestionsContext();
  const tags = useMemo(() => {
    const suggestedTags = (suggestions ?? []).map((s) => s.tag && {
      ...s.tag,
      suggested: true
    }).filter(isTruthy);
    const queryTagsData = (queryTags.data || []).filter((t) => !suggestedTags.find((st) => st.id === t.id || st.name.toLowerCase() === t.name.toLowerCase()) && !selectedTags.find((st) => st.id === t.id || st.name.toLowerCase() === t.name.toLowerCase())).map((t) => ({
      ...t,
      suggested: false
    }));
    const selTags = selectedTags.map((st) => ({
      ...st,
      suggested: st.isDraft
    }));
    const tagsWithCheckedState = [
      ...selTags,
      ...suggestedTags,
      ...queryTagsData
    ].map((tag) => {
      if (indeterminateTags.find((t) => t.id === tag.id)) {
        return {
          ...tag,
          checked: "indeterminate"
        };
      }
      if (selectedTags.find((t) => t.id === tag.id)) {
        return {
          ...tag,
          checked: true
        };
      }
      return {
        ...tag,
        checked: false
      };
    });
    return tagsWithCheckedState.filter((tag) => tag.name.toLowerCase().includes(inputState.value.toLowerCase())).sort((a, b) => {
      return a.name.localeCompare(b.name, void 0, {
        sensitivity: "base"
      });
    }).filter((tag, index, whole) => whole.findIndex((t) => tag.id === t.id || tag.name.toLowerCase() === t.name.toLowerCase()) === index);
  }, [
    queryTags.data,
    suggestions,
    indeterminateTags,
    selectedTags,
    inputState.value
  ]);
  const onSelect = async (tag, selected) => {
    if (!tag.isDraft) {
      onSelectProp?.(tag, selected);
    } else {
      mutationTagCreate.mutate({
        tagName: tag.name
      }, {
        onSuccess: (tag2) => {
          onSelectProp?.(tag2, selected);
          const suggestion = suggestions?.find((s) => tag2.id === s.id);
          if (suggestion) popSuggestion(suggestion);
        }
      });
    }
  };
  const formRef = useRef(null);
  const inputRef = useRef(null);
  const isModalInactive = useModalInactiveState(formRef);
  const shouldAutoFocus = !(isModalInactive || isMobileView);
  const onBlurInput = useRefocusOnBlur(!shouldAutoFocus);
  useFocusOnMount(inputRef, {
    condition: shouldAutoFocus
  });
  return /* @__PURE__ */ jsxs(StyledForm, {
    onSubmit,
    ref: formRef,
    children: [
      /* @__PURE__ */ jsxs(StyledModalHeader, {
        variant: "simple",
        padding: "12",
        children: [
          /* @__PURE__ */ jsx(Title, {
            children: "Choose Tags"
          }),
          /* @__PURE__ */ jsx(VisuallyHidden, {
            children: /* @__PURE__ */ jsxs(DialogDescription, {
              children: [
                "Select or unselect tags to add or remove from",
                " ",
                selectedResourcesCount === 1 ? "this item" : `selected items`,
                "."
              ]
            })
          }),
          /* @__PURE__ */ jsx(Modal.Close, {})
        ]
      }),
      /* @__PURE__ */ jsx(SubHeader, {
        children: /* @__PURE__ */ jsx(TextInput, {
          placeholder: isMobileView ? "Filter tags" : "Find tags or enter new tag name",
          startAdornment: /* @__PURE__ */ jsx(SearchIcon, {}),
          onChange: inputState.onChange,
          onBlur: onBlurInput,
          value: inputState.value,
          ref: inputRef,
          autoFocus: true,
          variant: "secondary",
          inputHeight: "40",
          endAdornment: inputState.value && /* @__PURE__ */ jsx(CloseButton, {
            type: "button",
            onClick: inputState.clear,
            children: /* @__PURE__ */ jsx(CloseIcon, {
              style: {
                width: 14,
                height: 14
              }
            })
          })
        })
      }),
      (selectedTags?.length > 0 || indeterminateTags?.length > 0) && !!onSelect && /* @__PURE__ */ jsx(SelectedTags, {
        selectedTags,
        indeterminateTags,
        onSelect
      }),
      /* @__PURE__ */ jsx(StyledScrollArea, {
        scrollbarVariant: "subtle",
        children: /* @__PURE__ */ jsx(Modal.Body, {
          padding: "12",
          style: {
            paddingTop: 0
          },
          children: /* @__PURE__ */ jsx(TagsList, {
            tags,
            onSelect,
            isLoading: queryTags.isLoading,
            searchQuery: inputState.value
          })
        })
      })
    ]
  });
};
const EditTagsModal = ({ children, ...props }) => {
  const { open, onOpenChange } = "open" in props ? props : {
    open: void 0,
    onOpenChange: void 0
  };
  const { modalProps } = useBoolState();
  const isOpen = open || modalProps.open;
  return /* @__PURE__ */ jsxs(Modal.Root, {
    open: isOpen,
    onOpenChange: onOpenChange || modalProps.onOpenChange,
    children: [
      /* @__PURE__ */ jsx(Modal.Trigger, {
        asChild: true,
        children
      }),
      /* @__PURE__ */ jsxs(Modal.Portal, {
        container: getDefaultRadixPortalContainer(),
        children: [
          /* @__PURE__ */ jsx(Modal.Overlay, {}),
          /* @__PURE__ */ jsx(Modal.Content, {
            respectMobileKeyboard: true,
            width: "sm+",
            children: /* @__PURE__ */ jsx(EditTagsModalContent, {
              ...props
            })
          })
        ]
      })
    ]
  });
};
const SelectedTags = ({ selectedTags, indeterminateTags, onSelect }) => {
  const [activeTags, setActiveTags] = useState([]);
  useEffect(() => {
    const nextTagIds = [
      ...selectedTags.map((tag) => tag.id),
      ...indeterminateTags.map((tag) => tag.id)
    ];
    if (nextTagIds.length <= activeTags.length) {
      setActiveTags(nextTagIds);
      return;
    }
    const newTags = nextTagIds.filter((id) => !activeTags?.includes(id));
    const newestTag = newTags[0];
    if (newestTag) {
      setTimeout(() => {
        const newTagElement = document.getElementById(`selected-tag-${newestTag}`);
        if (newTagElement) {
          newTagElement.scrollIntoView({
            behavior: "smooth",
            inline: "start"
          });
        }
      }, 100);
    }
    setActiveTags(nextTagIds);
  }, [
    selectedTags,
    indeterminateTags,
    activeTags
  ]);
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      /* @__PURE__ */ jsx(ScrollArea.Default, {
        style: {
          flexShrink: 0
        },
        orientation: "horizontal",
        scrollbarVariant: "subtle",
        children: /* @__PURE__ */ jsx(SelectedItemsContainer, {
          children: /* @__PURE__ */ jsxs(AnimatePresence, {
            children: [
              selectedTags.map((tag) => /* @__PURE__ */ jsxs(SelectedTag, {
                onClick: () => onSelect(tag, false),
                id: `selected-tag-${tag.id}`,
                children: [
                  tag.name,
                  /* @__PURE__ */ jsx(RemoveButton, {
                    children: /* @__PURE__ */ jsx(IconDesktop16Cross, {})
                  })
                ]
              }, tag.id)),
              indeterminateTags.map((tag) => /* @__PURE__ */ jsxs(SelectedTag, {
                isIndeterminate: true,
                onClick: () => onSelect(tag, false),
                id: `selected-tag-${tag.id}`,
                children: [
                  tag.name,
                  /* @__PURE__ */ jsx(RemoveButton, {
                    children: /* @__PURE__ */ jsx(IconDesktop16MiscMinus, {})
                  })
                ]
              }, `${tag.id}-indeterminate`))
            ]
          })
        })
      }),
      /* @__PURE__ */ jsx(Divider, {})
    ]
  });
};
const SelectedItemsContainer = styled(Flex).withConfig({
  displayName: "EditTagsModal__SelectedItemsContainer",
  componentId: "sc-7b52eb6e-4"
})([
  `gap:0.75rem;padding:0.75rem;`
]);
const SelectedTag = styled(motion(Flex)).attrs((p) => {
  return {
    ...framerAnimationFade,
    layout: "position",
    transition: {
      delay: 0,
      duration: 0.1
    },
    ...p
  };
}).withConfig({
  displayName: "EditTagsModal__SelectedTag",
  componentId: "sc-7b52eb6e-5"
})([
  `background-color:`,
  `;color:`,
  `;border-radius:0.5rem;padding:0.5rem;border:1px solid transparent;gap:0.5rem;height:30px;align-items:center;justify-content:center;white-space:nowrap;font-size:13px;font-weight:500;`,
  ``
], cssVar["color-bg-primary-reverse"], cssVar["color-text-primary-reverse"], (p) => p.isIndeterminate && css([
  `background-color:`,
  `;`
], cssVar["color-text-tertiary"]));
const RemoveButton = styled.button.withConfig({
  displayName: "EditTagsModal__RemoveButton",
  componentId: "sc-7b52eb6e-6"
})([
  `outline:none;`,
  `{&:hover{opacity:0.5;}}`
], mediaHover);
const Title = styled(Modal.Title).withConfig({
  displayName: "EditTagsModal__Title",
  componentId: "sc-7b52eb6e-7"
})([
  `font-size:16px;font-weight:500;`,
  `{font-size:14px;color:`,
  `;}`
], mediaMobile, cssVar["color-text-tertiary"]);
const StyledModalHeader = styled(Modal.BaseHeader).withConfig({
  displayName: "EditTagsModal__StyledModalHeader",
  componentId: "sc-7b52eb6e-8"
})([
  ``,
  `{padding-bottom:0;}`
], mediaMobile);

export { EditTagsModal as default };
//# sourceMappingURL=EditTagsModal.js.map
